/**
 * Sprint 24.17a — Live Activity Line data resolution (Pattern B).
 *
 * The pure matcher behind `useActiveAgentFor`: given the recent workspace
 * activity feed, decide whether an agent is *in-flight* on a given entity and,
 * if so, produce the props the LiveActivityLine renders.
 *
 * No backend schema change: tasks aren't an Activity target, so the
 * "Architect picked up Sprint X" Activity rides its task + agent identity in
 * `details`. This matcher reads that
 * convention AND a plain `regarding` match (for the future routine/node
 * consumers that CAN target natively). "In-flight" = matched AND recent.
 *
 * Pure TS (no React/DOM) — safe to import from Node test scripts.
 */

/** Window after which a pickup Activity stops counting as "in-flight". */
export const ACTIVE_ACTIVITY_FRESH_MS = 15 * 60_000;

/** Ramp-name → hex (the four pre-tested category tones; default = Architect purple). */
const RAMP_HEX: Record<string, string> = {
  purple: "#7F77DD",
  teal: "#1D9E75",
  coral: "#D85A30",
  pink: "#D4537E",
};

export function rampToHex(ramp: string | null | undefined): string {
  return (ramp && RAMP_HEX[ramp]) || "#7F77DD";
}

export interface LiveActivityState {
  agent: { node_id: string; name: string; icon: string; ramp_hex: string };
  verb: string;
  /** ISO timestamp the work started — drives the "age" label. */
  since: string;
  view_link?: { label: string; url: string };
}

/** Structural subset of an Activity row the matcher needs (test-friendly). */
export interface ActivityLike {
  occurred_at?: string | null;
  created_at?: string | null;
  actor_id?: string | null;
  actor_label?: string | null;
  details?: unknown;
  regarding?: Array<{ kind: string; id: string }>;
}

interface PickupDetails {
  trigger?: string;
  task_id?: string;
  verb?: string;
  agent?: { node_id?: string; name?: string; icon?: string; ramp?: string };
}

function asPickupDetails(details: unknown): PickupDetails | null {
  if (details && typeof details === "object") return details as PickupDetails;
  return null;
}

/** Does this Activity reference (entityKind, entityId)? */
function references(activity: ActivityLike, entityKind: string, entityId: string): boolean {
  // Native target match — works for library_node / task / routine / entity.
  if ((activity.regarding ?? []).some((r) => r.kind === entityKind && r.id === entityId)) return true;
  // Convention match — a task pickup carries the task id in details
  // (tasks can't be an Activity `regarding` target).
  const d = asPickupDetails(activity.details);
  if (entityKind === "task" && d?.task_id === entityId) return true;
  return false;
}

function timestampMs(activity: ActivityLike): number {
  const iso = activity.occurred_at ?? activity.created_at ?? null;
  const ms = iso ? Date.parse(iso) : NaN;
  return Number.isNaN(ms) ? 0 : ms;
}

/**
 * The most-recent in-flight Activity for (entityKind, entityId), mapped to
 * LiveActivityLine props — or null when no fresh work matches.
 */
export function pickActiveAgentActivity(
  activities: ActivityLike[],
  entityKind: string,
  entityId: string,
  opts: { nowMs?: number; freshMs?: number } = {},
): LiveActivityState | null {
  const nowMs = opts.nowMs ?? Date.now();
  const freshMs = opts.freshMs ?? ACTIVE_ACTIVITY_FRESH_MS;

  let best: ActivityLike | null = null;
  let bestMs = -1;
  for (const a of activities) {
    if (!references(a, entityKind, entityId)) continue;
    const ms = timestampMs(a);
    if (nowMs - ms > freshMs) continue; // stale → not in-flight
    if (ms > bestMs) {
      best = a;
      bestMs = ms;
    }
  }
  if (!best) return null;

  const d = asPickupDetails(best.details);
  const agent = d?.agent ?? {};
  const sinceIso = best.occurred_at ?? best.created_at ?? new Date(bestMs).toISOString();
  return {
    agent: {
      node_id: agent.node_id ?? best.actor_id ?? "",
      name: agent.name ?? best.actor_label ?? "Agent",
      icon: agent.icon ?? "ti-compass",
      ramp_hex: rampToHex(agent.ramp),
    },
    verb: d?.verb ?? "working",
    since: sinceIso,
  };
}
